import React, { useContext, useState, useRef, useEffect } from 'react';

import styled from 'styled-components';

import CheckboxSettingsField from './CheckboxSettingsField';
import InputSettingsField from './InputSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import Button from '../../misc/Button';
import Icon from '../../misc/Icon';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import RequiredLogicSettings from './RequiredLogicSettings';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import DecoratorSettings from './DecoratorSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import {
  StyledFieldWrapper,
  StyledAddonBefore,
  StyledAddonAfter,
  StyledButtonsWrapper,
  DividerLine,
} from './SettingsCommonElements';

import {
  isSettingHidden,
  isStyleSettingHidden,
  getMetadataOptions,
} from '../../../lib/settingsHelper';
import { validateReadableId } from '@builttocreate/engine-utils';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const StyledWrapper = styled.div`
	padding: 12px 0px;
	display: flex;
	flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

const FieldSettingsChartField = ({
  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,
  requiredLogic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  fieldSection,

  title,
  type,
  identifier,
  metadata,
  config,
  required,
  hidden,
  hiddenViews,
  disabled,

  x,
  y,
  width,
  height,

  yTitle,
  yMin,
  yMax,
  xTitle,
  xMin,
  xMax,

  primaryMaxHeight, //Max graph chart height
  primaryDisplayOnly, //Only show graph and hide line forms

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,
  margin,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onDuplicate,

  tipVisible,
  tipTitle,
  tipDescription,
  useReadableIds,

  decorators,
  onDecoratorCreate,
  onDecoratorChange,
  onDecoratorDelete,
}) => {
  const theme = useContext(ThemeContext);
  const [readableIdError, setReadableIdError] = useState('');

  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== fieldId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = fieldId;

  }, [fieldId, setReadableIdError]);


  const isTitleHidden = isSettingHidden(config, 'title');
  const isStylesHidden = isSettingHidden(config, 'styles');
  const isDisabledHidden = isSettingHidden(config, 'disabled');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isVisibilityHidden = isSettingHidden(config, 'hiddenViews');
  const isDecoratorsHidden = isSettingHidden(config, 'decorators');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isTooltipHidden = isSettingHidden(config, 'tooltip');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isIdHidden = isSettingHidden(config, '_id');

  const isPrimaryMaxHeightHidden = isSettingHidden(config, 'primaryMaxHeight');
  const isPrimaryDisplayOnlyHidden = isSettingHidden(
    config,
    'primaryDisplayOnly'
  );
  const isYTitleHidden = isSettingHidden(config, 'yTitle');
  const isYMaxHidden = isSettingHidden(config, 'yMax');
  const isYMinHidden = isSettingHidden(config, 'yMin');
  const isXTitleHidden = isSettingHidden(config, 'xTitle');
  const isXMaxHidden = isSettingHidden(config, 'xMax');
  const isXMinHidden = isSettingHidden(config, 'xMin');

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(
    config,
    'titleFontWeight'
  );
  const isTitleTextTransformHidden = isStyleSettingHidden(
    config,
    'titleTextTransform'
  );
  const isTitleTextDecorationHidden = isStyleSettingHidden(
    config,
    'titleTextDecoration'
  );

  //Text Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(
    config,
    'backgroundColor'
  );
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isHeaderOrFooter = fieldSection === 'header' || fieldSection === 'footer';

  const metadataOptions = getMetadataOptions(config);

  const stylesNode = (
    <>
      {isTitleFontSizeHidden &&
        isTitleFontColorHidden &&
        isTitleTextAlignHidden &&
        isTitleFontStyleHidden &&
        isTitleFontWeightHidden &&
        isTitleTextTransformHidden &&
        isTitleTextDecorationHidden ? null : (
        <>
          <AccordionSettings isOpen title="Title" height={170}>
            <FieldStyleSettings
              id={`${fieldId}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontSize: value,
                  }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontColor: value,
                  }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleTextAlign: value,
                  }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontStyle: value,
                  }),

                fontWeight: titleFontWeight,
                onFontWeightChange: isTitleFontWeightHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleFontWeight: value,
                  }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleTextTransform: value,
                  }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    titleTextDecoration: value,
                  }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {isFontSizeHidden &&
        isFontColorHidden &&
        isTextAlignHidden &&
        isFontStyleHidden &&
        isFontWeightHidden &&
        isTextTransformHidden &&
        isTextDecorationHidden &&
        isTextOverflowHidden ? null : (
        <>
          <AccordionSettings isOpen title="Text" height={170}>
            <FieldStyleSettings
              id={`${fieldId}-field`}
              {...{
                fontSize,
                onFontSizeChange: isFontSizeHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    fontSize: value,
                  }),

                fontColor,
                onFontColorChange: isFontColorHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    fontColor: value,
                  }),

                textAlign,
                onTextAlignChange: isTextAlignHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    textAlign: value,
                  }),

                fontStyle,
                onFontStyleChange: isFontStyleHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    fontStyle: value,
                  }),

                fontWeight,
                onFontWeightChange: isFontWeightHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    fontWeight: value,
                  }),

                textTransform,
                onTextTransformChange: isTextTransformHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    textTransform: value,
                  }),

                textDecoration,
                onTextDecorationChange: isTextDecorationHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    textDecoration: value,
                  }),

                textOverflow,
                onTextOverflowChange: isTextOverflowHidden
                  ? null
                  : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    textOverflow: value,
                  }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {isBorderColorHidden &&
        isBorderRadiusHidden &&
        isBorderWidthHidden &&
        isBackgroundColorHidden &&
        isMarginHidden &&
        isPaddingHidden &&
        isPositionHidden ? null : (
        <AccordionSettings isOpen title="Field" height={400}>
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{
              borderColor,
              onBorderColorChange: isBorderColorHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  borderColor: value,
                }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  borderRadius: value,
                }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  borderWidth: value,
                }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  backgroundColor: value,
                }),

              padding,
              onPaddingChange: isPaddingHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  padding: value,
                }),

              margin,
              onMarginChange: isMarginHidden
                ? null
                : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  margin: value,
                }),

              zIndex,
              onZIndexChange: isZIndexHidden
                ? null
                : (value) => (value
                  ? onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    zIndex: value,
                  })
                  : ''),
            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                  [field]: value,
                }),
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        type="text"
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid: Can't be duplicate or start with numerics, use reserved ES6 keywords, or have spaces.`);

          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );


  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <TitleSettings
            {...{
              title,
              titleDisplay,
            }}
            onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
          />
        </>
      )}
      {isPrimaryMaxHeightHidden &&
        isPrimaryDisplayOnlyHidden &&
        isYTitleHidden &&
        isYMaxHidden &&
        isYMinHidden &&
        isXTitleHidden &&
        isXMaxHidden &&
        isXMinHidden &&
        isRequiredHidden &&
        isDisabledHidden ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
      )}
      {isPrimaryMaxHeightHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            addonBefore={<Icon icon={ThemeIcons.fields.chart} />}
            addonAfter={<StyledAddonAfter>max height</StyledAddonAfter>}
            fieldId={fieldId}
            value={primaryMaxHeight}
            type="number"
            onChange={(fieldId, value) => {
              const nextPrimaryMaxHeight = value || 280;
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                primaryMaxHeight: parseFloat(nextPrimaryMaxHeight, 10),
              });
            }}
          />
        </StyledFieldWrapper>
      )}
      {isYTitleHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            value={yTitle}
            label="Y"
            addonBefore={<StyledAddonBefore>Title</StyledAddonBefore>}
            type="text"
            onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
              yTitle: value,
            })}
          />
        </StyledFieldWrapper>
      )}
      {isYMinHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            value={yMin}
            addonBefore={<StyledAddonBefore>Min</StyledAddonBefore>}
            type="number"
            onChange={(fieldId, value) => {
              const nextValue = value || 0;
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                yMin: parseFloat(nextValue, 10),
              });
            }}
          />
        </StyledFieldWrapper>
      )}
      {isYMaxHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            value={yMax}
            addonBefore={<StyledAddonBefore>Max</StyledAddonBefore>}
            type="number"
            onChange={(fieldId, value) => {
              const nextValue = value || 0;
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                yMax: parseFloat(nextValue, 10),
              });
            }}
          />
        </StyledFieldWrapper>
      )}
      {isXTitleHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            value={xTitle}
            label="X"
            addonBefore={<StyledAddonBefore>Title</StyledAddonBefore>}
            type="text"
            onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
              xTitle: value,
            })}
          />
        </StyledFieldWrapper>
      )}
      {isXMinHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            value={xMin}
            addonBefore={<StyledAddonBefore>Min</StyledAddonBefore>}
            type="number"
            onChange={(fieldId, value) => {
              const nextValue = value || 0;
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                xMin: parseFloat(nextValue, 10),
              });
            }}
          />
        </StyledFieldWrapper>
      )}
      {isXMaxHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            value={xMax}
            addonBefore={<StyledAddonBefore>Max</StyledAddonBefore>}
            type="number"
            onChange={(fieldId, value) => {
              const nextValue = value || 0;
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                xMax: parseFloat(nextValue, 10),
              });
            }}
          />
        </StyledFieldWrapper>
      )}
      {isPrimaryDisplayOnlyHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id={fieldId}
            icon={ThemeIcons.settings.chartPrimaryDisplayOnly}
            iconColor={
              !primaryDisplayOnly ? theme.primaryColor : theme.borderColor
            }
            iconSize="sm"
            label="Display Line Details"
            selected={!primaryDisplayOnly}
            onChange={(selected) => {
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                primaryDisplayOnly: !selected,
              });
            }}
          />
        </StyledFieldWrapper>
      )}
      {isDisabledHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.disabled}
            iconColor={disabled ? theme.primaryColor : theme.borderColor}
            id="disabled"
            label="Read-only"
            selected={disabled}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
              disabled: selected,
            })}
          />
        </StyledFieldWrapper>
      )}
      {isRequiredHidden ? null : (
        !isHeaderOrFooter && (
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.required}
              iconColor={required ? theme.dangerColor : theme.borderColor}
              iconSize="1x"
              id="required"
              label="Required"
              selected={required}
              onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                required: selected,
              })}
            />
          </StyledFieldWrapper>
        )
      )}
      {isHeaderOrFooter || fields.length < 2 ? null : (
        <>
          <SettingsLabel>Required Logic</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledFieldWrapper>
            <RequiredLogicSettings
              fileId={fileId}
              targetLabel=" field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={requiredLogic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { requiredLogic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {(isHiddenHidden && (isLogicHidden || fields.length < 2)) ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Conditional Logic</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            {isLogicHidden || fields.length < 2 ? null : (
              <StyledFieldWrapper>
                <ConditionalLogicSettings
                  fileId={fileId}
                  targetLabel=" field"
                  targetFieldId={fieldId}
                  sortedPages={sortedPages}
                  fields={fields}
                  logic={logic}
                  onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    logic: nextLogic,
                  })}
                />
              </StyledFieldWrapper>
            )}
            {isHiddenHidden ? null : (
              <StyledFieldWrapper>
                <CheckboxSettingsField
                  icon={ThemeIcons.settings.hide}
                  iconColor={hidden ? theme.warningColor : theme.borderColor}
                  id="hidden"
                  label="Hidden"
                  selected={hidden}
                  onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                    hidden: selected,
                  })}
                />
              </StyledFieldWrapper>
            )}
          </>
        )
      )}
      {isVisibilityHidden ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Visibility</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('desktop') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsDesktop"
                label="Hide on desktop"
                selected={hiddenViews?.includes('desktop') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'desktop']
                    : (hiddenViews || []).filter(v => v !== 'desktop');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('mobile') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsMobile"
                label="Hide on mobile"
                selected={hiddenViews?.includes('mobile') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'mobile']
                    : (hiddenViews || []).filter(v => v !== 'mobile');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('pdf') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsPdf"
                label="Hide on pdf"
                selected={hiddenViews?.includes('pdf') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'pdf']
                    : (hiddenViews || []).filter(v => v !== 'pdf');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
          </>
        )
      )}
      {isDecoratorsHidden ? null : (
        <>
          <SettingsLabel>Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <DecoratorSettings
            decorators={decorators}
            onCreate={(data) => onDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
            onChange={(decoratorAction, data) => onDecoratorChange(fieldId, fieldIdentifier, fieldPositionId, decoratorAction, data)}
            onDelete={(decoratorAction) => onDecoratorDelete(fieldId, fieldIdentifier, fieldPositionId, decoratorAction)}
          />
        </>
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata
                ? { ...metadata, ...changes }
                : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, {
                metadata: nextMetadata,
              });
            }}
          />
        </>
      )}
      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}
      {isTooltipHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings title="Tooltip" height={166}>
            <TooltipSettings
              {...{
                tipVisible,
                tipTitle,
                tipDescription,
              }}
              onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
            />
          </AccordionSettings>
        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
                identifier: value,
              })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />
            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => onDuplicate(fieldId, fieldPositionId)}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={ThemeIcons.fields.chart}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );
};

export default FieldSettingsChartField;
